import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Scale, Eye, Sparkles } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { PublicLayout, Logo } from "../../components/site/Layout";
import { SERVICES, PROCESS, FAQS } from "../../content";

const PRINCIPLES = [
  [Scale, "Prices you can trust", "Every price comes from our configured price list. AI never invents prices."],
  [Eye, "Nothing hidden", "Altiora charges, taxes, recurring fees and third-party costs are shown separately."],
  [Sparkles, "AI that assists, people who deliver", "AI drafts your concept. Our team builds and tests the real website."],
  [ShieldCheck, "Secure by design", "Verified payments, protected data, and we never ask for your passwords."],
];

export default function Home() {
  return (
    <PublicLayout>
      <section className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-20 md:pt-20 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="eyebrow" data-testid="home-eyebrow">Ideas · Insights · Solutions</p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#062a20] leading-[1.04]" data-testid="home-hero-title">
              Your business website, <em className="text-[#9a7322] not-italic">configured in minutes</em>, built properly by people.
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              Describe your business in plain words. Our AI configurator recommends the right features, shows transparent pricing and designs your website — then you edit, preview and publish it yourself.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/build" className="btn-emerald" data-testid="home-build-cta">Build Your Website <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/presentations" className="btn-gold" data-testid="home-pres-cta">Build Your Presentation <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/how-it-works" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-[#0b3d2e] border border-[#0b3d2e]/20 rounded-md hover:bg-[#0b3d2e]/5" data-testid="home-how-cta">See how it works</Link>
            </div>
            <p className="mt-5 text-xs text-slate-500">No payment needed to explore. See your price before you decide.</p>
          </div>
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: ".15s" }}>
            <div className="absolute -inset-6 bg-[#f4efe2] rounded-xl -rotate-2 hidden sm:block" aria-hidden />
            <div className="relative card-lux !p-8 text-center">
              <Logo className="h-44 mx-auto" />
              <div className="gold-rule my-6" />
              <ul className="text-left space-y-3 text-sm text-slate-700">
                {["Plain-English questions — only the relevant ones", "Three packages compared side by side", "Live price with taxes, advance and balance", "Design concept with colour, theme and layout controls"].map((t) => (
                  <li key={t} className="flex gap-3"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#b8892b] shrink-0" />{t}</li>))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="surface-emerald grain">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {PRINCIPLES.map(([I, t, d]) => (
            <div key={t} className="space-y-3"><I className="w-6 h-6 text-[#d9b44c]" /><h3 className="text-xl text-[#f6ecd2]">{t}</h3><p className="text-sm text-[#cfc6ad]">{d}</p></div>))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><p className="eyebrow">Our services</p><h2 className="mt-3 text-3xl md:text-4xl font-semibold text-[#062a20]">One partner for ideas, insights and solutions.</h2>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0b3d2e]" data-testid="home-services-link">All services <ArrowRight className="w-4 h-4" /></Link></div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {SERVICES.slice(0, 4).map((s) => (
              <Link key={s.key} to={s.cta} className={`card-lux block ${s.live ? "sm:col-span-2 border-[#b8892b]/50" : ""}`} data-testid={`home-service-${s.key}`}>
                {s.live && <span className="eyebrow">Available online now</span>}
                <h3 className="mt-1 text-2xl text-[#062a20]">{s.title}</h3><p className="mt-2 text-sm text-slate-600">{s.text}</p>
              </Link>))}
          </div>
        </div>
      </section>

      <section className="bg-[#f4efe2]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 md:py-28">
          <p className="eyebrow">The process</p><h2 className="mt-3 text-3xl md:text-4xl font-semibold text-[#062a20] max-w-2xl">From a conversation to a launched website.</h2>
          <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {PROCESS.map(([t, d], i) => (
              <li key={t} className="border-t border-[#0b3d2e]/20 pt-5"><span className="font-cinzel text-sm text-[#9a7322]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 text-xl text-[#062a20]">{t}</h3><p className="mt-2 text-sm text-slate-600">{d}</p></li>))}
          </ol>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-20 md:py-28">
        <p className="eyebrow">Questions</p><h2 className="mt-3 text-3xl md:text-4xl font-semibold text-[#062a20]">Answers before you start.</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="home-faq">
          {FAQS.slice(0, 5).map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`}><AccordionTrigger className="text-left text-base" data-testid={`home-faq-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>))}
        </Accordion>
        <Link to="/faqs" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0b3d2e]">All FAQs <ArrowRight className="w-4 h-4" /></Link>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="surface-emerald grain rounded-xl px-8 py-14 md:px-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div><h2 className="text-3xl md:text-4xl text-[#f6ecd2]">See your website's price in minutes.</h2><p className="mt-3 text-[#cfc6ad]">Free to explore. No commitment until you accept a quotation.</p></div>
          <Link to="/build" className="btn-gold shrink-0" data-testid="home-bottom-cta">Build Your Website <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </PublicLayout>
  );
}
